import { useEffect, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconDotsOutline18 as Dots,
	IconBarsFilterOutline18 as ListFilter,
	IconPenOutline18 as Pen,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { CustomView } from "@productlane/zero/schema"
import { cn } from "@productlane/lib/src/cn"
import { IconButton } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { NavRowActions } from "../helpCenter/nav-row-actions"
import { useActiveRoadmapView } from "./filter/roadmap-views"

const ROW =
	"flex h-[1.9rem] w-full min-w-0 items-center gap-2 rounded-md pl-2 pr-2 text-sm font-medium"
const SELECTED = "bg-gray-100 text-primary dark:bg-gray-800"
const IDLE = "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50"

/** Saved roadmap filters, listed under the roadmap's own views. */
export function RoadmapViewsNav({
	onOpenView,
	onSelect,
}: {
	onOpenView: (view: CustomView) => void
	onSelect?: () => void
}) {
	const [views] = useQuery(queries.roadmapViews())
	const activeViewId = useActiveRoadmapView((s) => s.activeViewId)

	if (views.length === 0) return null

	return (
		<ul className="mt-4 flex list-none flex-col gap-px px-2.5">
			<li className="px-2 pb-1 text-xs font-medium text-tertiary">Views</li>
			{views.map((view) => (
				<RoadmapViewRow
					key={view.id}
					view={view}
					isSelected={view.id === activeViewId}
					onOpen={() => {
						onOpenView(view)
						onSelect?.()
					}}
				/>
			))}
		</ul>
	)
}

function RoadmapViewRow({
	view,
	isSelected,
	onOpen,
}: {
	view: CustomView
	isSelected: boolean
	onOpen: () => void
}) {
	const zero = useZero()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const renamingViewId = useActiveRoadmapView((s) => s.renamingViewId)
	const setRenamingViewId = useActiveRoadmapView((s) => s.setRenamingViewId)
	const setActiveViewId = useActiveRoadmapView((s) => s.setActiveViewId)
	const isRenaming = renamingViewId === view.id
	const [name, setName] = useState(view.name)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (!isRenaming) return
		setName(view.name)
		requestAnimationFrame(() => inputRef.current?.select())
	}, [isRenaming, view.name])

	const saveName = async () => {
		setRenamingViewId(null)
		const next = name.trim()
		if (!next || next === view.name) return
		await mutateOk(
			zero.mutate(mutators.customViews.update({ id: view.id, name: next })),
		)
	}

	const remove = async () => {
		if (isSelected) setActiveViewId(null)
		await mutateOk(zero.mutate(mutators.customViews.delete({ id: view.id })))
	}

	return (
		<li className="group/item relative">
			{isRenaming ? (
				<div className={cn(ROW, SELECTED)}>
					<ListFilter className="size-3.5 flex-none text-secondary" />
					<input
						ref={inputRef}
						aria-label="View name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						onBlur={() => void saveName()}
						onKeyDown={(e) => {
							if (e.key === "Enter") e.currentTarget.blur()
							if (e.key === "Escape") {
								setName(view.name)
								setRenamingViewId(null)
							}
						}}
						className="min-w-0 flex-1 bg-transparent text-sm font-medium text-primary outline-none"
					/>
				</div>
			) : (
				<button
					type="button"
					onClick={onOpen}
					aria-current={isSelected ? "page" : undefined}
					className={cn(ROW, isSelected ? SELECTED : IDLE)}
				>
					<ListFilter className="size-3.5 flex-none text-secondary" />
					<span className="min-w-0 flex-1 truncate text-start">
						{view.name}
					</span>
				</button>
			)}
			{canEdit && !isRenaming && (
				<NavRowActions className="right-0" isSelected={isSelected}>
					<DropdownMenu
						className="w-48"
						contentProps={{ align: "end" }}
						trigger={
							<IconButton
								Icon={Dots}
								variant="ghost"
								size="md"
								iconStrokeWidth={1.75}
								iconClassName="h-3 w-3"
								className="relative text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
								type="button"
								aria-label={`${view.name} actions`}
							/>
						}
					>
						<DropdownMenuItem
							Icon={Pen}
							onClick={() => setRenamingViewId(view.id)}
						>
							Rename
						</DropdownMenuItem>
						<DropdownMenuItem Icon={Trash} onClick={() => void remove()}>
							Delete
						</DropdownMenuItem>
					</DropdownMenu>
				</NavRowActions>
			)}
		</li>
	)
}
